/// <reference types="intercom-web" />
/// <reference types="jquery.colorbox" />
/// <reference types="webpack-env" />

import type { Faro } from "@grafana/faro-react";
import * as Sentry from "@sentry/react";
import type { IConfig as UnleashConfig } from "@unleash/proxy-client-react";
import Pusher from "pusher-js";
import * as React from "react";
import {
  type NavigateOptions,
  type To,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { Subscriber } from "@/auto-converted/lib/Subscriber";
import { prefetchCurrentUser } from "@/stores/CurrentUser";
import { prefetchInterfaceSettings } from "@/stores/InterfaceSetting";
import { prefetchLoggedInStatus } from "@/stores/LoggedInStatus";
import type { CVPartnerReactRoute, Options, Router } from "@/types";
import { getTokenHeaders } from "@/util/fetch";
import { skipFetchingUser } from "@/util/getCvPartner";
import { invariant } from "@/util/invariant";
import { renderRouter } from "@/util/react";
import { I18n as I18nObject } from "@/util/translations";
import { createFaro } from "./metrics";

import "jquery";
import "jquery-colorbox";

declare global {
  var I18n: typeof I18nObject;
}

globalThis.I18n = I18nObject;

type ReactRoute = new (options: Options) => CVPartnerReactRoute;

export class Cvpartner {
  faro?: Faro;
  subscriber?: Subscriber;
  unleashConfig?: UnleashConfig;
  error?: unknown;
  private router: Router | null = null;
  isLoggedIn = false;
  canFetchCurrentUser = false;
  aws_region = "eu-west-1";

  constructor(private readonly reactRoute: ReactRoute) {}

  init(options: Options) {
    this.isLoggedIn = options.current_user_id != null;
    this.canFetchCurrentUser = this.isLoggedIn && !skipFetchingUser();

    if (this.canFetchCurrentUser) {
      void Promise.all([prefetchCurrentUser(), prefetchInterfaceSettings()]);
    } else {
      void prefetchLoggedInStatus();
    }

    if (options.environment !== "test") {
      try {
        this.faro = createFaro(options);
        if (options.unleash_client_options) {
          const unleash_url = new URL(
            options.unleash_client_options.path,
            window.location.origin,
          );

          this.unleashConfig = {
            ...options.unleash_client_options,
            url: unleash_url.href,
          };
        }
        const headers = getTokenHeaders();
        const pusher = new Pusher(options.pusher_key, {
          auth: { headers },
          cluster: options.pusher_cluster,
        });
        this.subscriber = new Subscriber(pusher);
      } catch (error) {
        this.error = error;
        this.subscriber = new Subscriber();
      }

      if (options.sentry_dsn && options.sentry_dsn.trim() !== "") {
        Sentry.init({
          dsn: options.sentry_dsn,
          environment: options.environment || "unknown",
          release: options.release || "unknown",
          beforeSend:
            process.env.NODE_ENV === "development"
              ? (event, hint) => {
                  const message =
                    hint.originalException ??
                    hint.syntheticException ??
                    event.message;

                  if (event.level === "fatal" || event.level === "error") {
                    console.error(message);
                  } else {
                    console.log(message);
                  }

                  return event;
                }
              : undefined,
          integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
              useEffect: React.useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
          ],
        });
        // This is useful for debugging
        (window as any).Sentry = Sentry;

        if (this.error) {
          Sentry.captureException(this.error);
          this.faro?.api.pushError(this.error as Error);
        }
      }
    } else {
      this.subscriber = new Subscriber();
    }

    if (options.aws_region) {
      this.aws_region = options.aws_region;
    }

    const route = new this.reactRoute(options);

    const { router, selector } = route.render();

    renderRouter(router, selector);

    this.router = router;
  }

  navigate(to: number): Promise<void>;
  navigate(to: To | null, opts?: NavigateOptions): Promise<void>;
  async navigate(to: number | To | null, opts?: NavigateOptions) {
    const { router } = this;

    invariant(router, "Router is not initialized");

    if (typeof to === "number") {
      await router.navigate(to);
    } else {
      await router.navigate(to, opts);
    }
  }

  subscribeToRouter(callback: Parameters<Router["subscribe"]>[0]) {
    const { router } = this;

    invariant(router, "Router is not initialized");

    return router.subscribe(callback);
  }
}

export function setCvPartner(instance: Cvpartner) {
  // @ts-expect-error - Rails SSR expects this to be a global
  window.cvpartner = instance;
}

// Whenever we start using html history instead of hash history, this needs to
// be removed.
const { location } = window;
if (location.hash[0] === "#" && location.hash[1] !== "/") {
  location.hash = `#/${location.hash.slice(1)}`;
}
